/*
 * Copyright 2019 T-Mobile US, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
  @module: tmo-poptoken-builder
  @description: Defines the functions for the PoP Token Builder.
  @version: 1.0
**/
var rs = require("jsrsasign");

// In-memory fallback for the guest token. Stays populated even if sessionStorage
// gets cleared mid-session. Reads must check sessionStorage first; writes update
// both. Lost on full page reload.
var cachedAnonymousToken = null;

// Single-flight guard: prevents overlapping guest-token refresh requests when
// multiple callers (boot-time index.js + request-time interceptor) race to
// refresh at the same time.
let anonymousTokenRefreshPromise = null;

// Refresh the guest JWT this many ms before `exp` so MaintenanceFlag (and
// other anonymous calls) do not race an already-expired token to the gateway.
export const ANONYMOUS_TOKEN_EXPIRY_SKEW_MS = 60 * 1000;

function storeAnonymousToken(token) {
  if (!token) return;
  cachedAnonymousToken = token;
  sessionStorage.setItem("anonymous_token", token);
}

function readAnonymousToken() {
  var token = sessionStorage.getItem("anonymous_token");
  if (token) return token;
  if (cachedAnonymousToken) {
    if (isAnonymousTokenExpired(cachedAnonymousToken)) {
      cachedAnonymousToken = null;
      return null;
    }
    sessionStorage.setItem("anonymous_token", cachedAnonymousToken);
    return cachedAnonymousToken;
  }
  return null;
}

export function clearAnonymousToken() {
  cachedAnonymousToken = null;
  sessionStorage.removeItem("anonymous_token");
  anonymousTokenRefreshPromise = null;
}

export async function forceRefreshAnonymousToken() {
  clearAnonymousToken();
  return ensureFreshAnonymousToken();
}

export function isAnonymousAuthRetryable(error) {
  if (!error || !error.config || error.config._retryGuestAuth) {
    return false;
  }

  var status = error.response && error.response.status;
  if (status !== 400 && status !== 401) {
    return false;
  }

  var url = error.config.url || "";
  if (!checkIfAnonymousRequest(url)) {
    return false;
  }

  var urlLower = url.toLowerCase();
  if (urlLower.indexOf("asset/") === -1 && urlLower.indexOf("/asset") === -1) {
    return false;
  }

  var method = (error.config.method || "get").toLowerCase();
  return method === "get" || method === "head";
}

export const generateKeys = () =>
{
  if (sessionStorage.getItem("pvtkey") === null || sessionStorage.getItem("pubkey") === null) {
    var keypair = rs.KEYUTIL.generateKeypair("RSA", 1024);
    sessionStorage.setItem("pubkey", rs.KEYUTIL.getPEM(keypair.pubKeyObj));
    sessionStorage.setItem(
      "pvtkey",
      rs.KEYUTIL.getPEM(keypair.prvKeyObj, "PKCS1PRV")
    );
  }
}

export async function generatePopTokenValue(config) {
  if (sessionStorage.getItem("pvtkey") === null || sessionStorage.getItem("pubkey") === null) {
    var keypair = rs.KEYUTIL.generateKeypair("RSA", 1024);
    sessionStorage.setItem("pubkey", rs.KEYUTIL.getPEM(keypair.pubKeyObj));
    sessionStorage.setItem("pvtkey", rs.KEYUTIL.getPEM(keypair.prvKeyObj, "PKCS1PRV"));
  }

  var isAnonymousRequest = checkIfAnonymousRequest(config.url);

  var accessToken;
  if (isAnonymousRequest) {
    accessToken = await ensureFreshAnonymousToken();
  } else {
    accessToken = sessionStorage.getItem("TMO_PROMO_TOKEN_KEY");
  }

  var authorizationHeader = "Bearer " + accessToken;

  var ehtsKeyValuMap = new Map();
  ehtsKeyValuMap.set("Authorization", authorizationHeader);
  ehtsKeyValuMap.set("http-method", config.method.toLocaleUpperCase());

  var popToken = buildPopToken(ehtsKeyValuMap);

  if (isAnonymousRequest) {
    config.headers["Authorization"] = authorizationHeader;
  }

  config.headers["X-Authorization"] = popToken;

  console.log("[guest-token] generatePopTokenValue -> outgoing", {
    url: config.url,
    isAnonymousRequest: isAnonymousRequest,
    authorizationPresent: !!config.headers["Authorization"],
    sessionAnonHas: !!sessionStorage.getItem("anonymous_token"),
    cacheAnonHas: !!cachedAnonymousToken,
  });

  return config;
}

function isAnonymousTokenExpired(token) {
  if (!token) return true;
  try {
    const expirationTime = JSON.parse(window.atob(token.split(".")[1])).exp;
    return Date.now() >= expirationTime * 1000 - ANONYMOUS_TOKEN_EXPIRY_SKEW_MS;
  } catch (e) {
    return true;
  }
}

export async function ensureFreshAnonymousToken() {
  var existingAnonymousToken = readAnonymousToken();
  if (existingAnonymousToken && !isAnonymousTokenExpired(existingAnonymousToken)) {
    return existingAnonymousToken;
  }

  if (!anonymousTokenRefreshPromise) {
    console.log("[guest-token] ensureFreshAnonymousToken -> starting refresh");
    anonymousTokenRefreshPromise = requestAnonymousTokenAsync().finally(() => {
      anonymousTokenRefreshPromise = null;
    });
  } else {
    console.log("[guest-token] ensureFreshAnonymousToken -> awaiting in-flight refresh");
  }

  const refreshedToken = await anonymousTokenRefreshPromise;

  if (!refreshedToken) {
    throw new Error("Unable to obtain anonymous access token");
  }

  return refreshedToken;
}

export const deleteKeys = () =>
{
    sessionStorage.removeItem("pubkey");
    sessionStorage.removeItem("pvtkey"); 
}

export const ensureKeysExist = () => {
  if (sessionStorage.getItem("pvtkey") === null || sessionStorage.getItem("pubkey") === null) {
    generateKeys();
  }

  return sessionStorage.getItem("pubkey");
}

export const generatePopToken = () => { 

  var ehtsKeyValuMap = new Map();
  var authorizationValue = sessionStorage.getItem("anonymous_token");

  ehtsKeyValuMap.set("Authorization", "Bearer " + authorizationValue);
  ehtsKeyValuMap.set("http-method", "POST");

  var popToken = buildPopToken(ehtsKeyValuMap);  

  return popToken;
}

export function CheckIfAllQueryStringsExist(url, qsCollection) {
  for (var i = 0; i < qsCollection.length; i++) {
      if (url.indexOf(qsCollection[i]) === -1) {
          return false;
      }
  }
  return true;
}

export function checkIfAnonymousRequest(url) {

  var anonymousRequestURI = [
    "/auth/code",
    "/activateaaatoken",
    "/auth/getdnssettingv2",
    "/auth/dnssetting",
  ];

  var clientAsset = ["client", "asset"];
  var offerAsset = ["offer", "asset"];
  
  var clientAssetPresent = CheckIfAllQueryStringsExist(url, clientAsset);
  if (clientAssetPresent) {
    return true;
  }

  var offerAssetPresent = CheckIfAllQueryStringsExist(url, offerAsset);
  if (offerAssetPresent) {
    return true;
  }
  
  if (url.split("/").some((el) => el === "faq")) {
    return true;
  }

  if (url.split("/").some((el) => el === "details")) {
    return true;
  }

  if (url.split("/").some((el) => el === "clientrecaptchavisible")) {
    return true;
  }

  if (url.split("/").some((el) => el === "images")) {
    return true;
  }

  return anonymousRequestURI.some((uri) => url.includes(uri.toLowerCase()));
}


// Async fetch-based token request. Called once at app startup (index.js) so
// the token is stored before any page API calls fire. Returns a Promise that
// resolves when the token is stored (or rejects on network error).
export const requestAnonymousTokenAsync = async () => {
  var megClientId = process.env.REACT_APP_MEG_CLIENT_ID;
  var anonymousTokenUrl = process.env.REACT_APP_ANONYMOUS_TOKEN_URL;
  var ehtsKeyValueMapForPop = new Map();
  ehtsKeyValueMapForPop.set("http-method", "POST");

  generateKeys();

  var popForAnonymousTokenCall = buildPopToken(ehtsKeyValueMapForPop);

  var inputData = {
    client_id: megClientId,
    cnf: ensureKeysExist(),
  };

  console.log("[guest-token] requestAnonymousTokenAsync -> fetch POST", {
    url: anonymousTokenUrl,
    clientId: megClientId,
  });

  try {
    const response = await fetch(anonymousTokenUrl, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
        "X-Authorization": popForAnonymousTokenCall,
        "Origin": window.location.origin,
      },
      body: JSON.stringify(inputData),
    });

    if (response.ok) {
      const data = await response.json();
      storeAnonymousToken(data.access_token);
      console.log("[guest-token] requestAnonymousTokenAsync stored token", {
        tokenPresent: !!data.access_token,
      });
      return data.access_token;
    } else {
      console.warn("[guest-token] requestAnonymousTokenAsync non-200", { status: response.status });
      return null;
    }
  } catch (err) {
    console.error("[guest-token] requestAnonymousTokenAsync fetch failed", err);
    return null;
  }
};

// Sync fallback kept for the checkIfAnonymousTokenExpired path — only reached
// if the async startup fetch hasn't completed yet (rare) or the token expired
// mid-session. Sync XHR is unreliable on Android WebView so we log a warning.
export const requestAnonymousToken = () => {
  var megClientId = process.env.REACT_APP_MEG_CLIENT_ID;
  var anonymousTokenUrl = process.env.REACT_APP_ANONYMOUS_TOKEN_URL;
  var ehtsKeyValueMapForPop = new Map();
  ehtsKeyValueMapForPop.set("http-method", "POST");

  generateKeys();

  var popForAnonymousTokenCall = buildPopToken(ehtsKeyValueMapForPop);

  var inputData = {
    client_id: megClientId,
    cnf: ensureKeysExist(),
  };

  console.warn("[guest-token] requestAnonymousToken (sync fallback) -> POST", {
    url: anonymousTokenUrl,
    clientId: megClientId,
  });

  const xhr = new XMLHttpRequest();
  xhr.open("POST", anonymousTokenUrl, false);
  xhr.setRequestHeader("Content-type", "application/json");
  xhr.setRequestHeader("X-Authorization", popForAnonymousTokenCall);

  xhr.onload = function () {
    try {
      var response = JSON.parse(xhr.responseText);
      if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
        storeAnonymousToken(response.access_token);
        console.log("[guest-token] sync fallback stored token from onload");
      }
    } catch (e) {
      console.error("[guest-token] sync fallback onload parse error", e);
    }
  };
  xhr.onerror = function () {
    console.error("[guest-token] sync fallback xhr.onerror");
    sessionStorage.removeItem("anonymous_token");
  };

  try {
    xhr.send(JSON.stringify(inputData));
  } catch (sendErr) {
    console.error("[guest-token] sync fallback xhr.send threw", sendErr);
  }

  // Inline backup read for Android WebView where onload doesn't fire.
  if (!readAnonymousToken() && xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
    try {
      var inlineResponse = JSON.parse(xhr.responseText);
      if (inlineResponse && inlineResponse.access_token) {
        storeAnonymousToken(inlineResponse.access_token);
        console.log("[guest-token] sync fallback stored token from inline read");
      }
    } catch (e) {
      console.error("[guest-token] sync fallback inline read parse error", e);
    }
  } else if (!readAnonymousToken()) {
    console.warn("[guest-token] sync fallback: no token stored after send", {
      status: xhr.status,
      readyState: xhr.readyState,
    });
  }
};

const buildPopToken = (ehtsKeyValuMap) => {
  /*
     param ehtsKeyValuMap - Map to be signed
     param privateKeyPemString - Private Key PEM string
    */
  var objClaim = {};
  var strEhts = "";
  var strToHash = "";
  var strToken = "";
  var curTime = "now";
  var currentTimeInSeconds = Math.floor(new Date().getTime() / 1000);
  var expTime = currentTimeInSeconds + 2 * 60; // token is valid for 2 minutes

  for (var [key, value] of ehtsKeyValuMap) {
    if (strEhts.length > 0) {
      strEhts = strEhts + ";";
    }
    strEhts = strEhts + key;
    strToHash = strToHash + value;
  }

  var strHash = rs.KJUR.crypto.Util.sha256(strToHash);
  var strEdtsHashB64U = rs.hextob64u(strHash);

  var strUniq = createGUID();

  if (strEdtsHashB64U.length > 0) {
    objClaim.edts = strEdtsHashB64U;
  }

  objClaim.v = "1";

  if (expTime !== "") {
    objClaim.exp = expTime;
  }

  if (strEhts.length > 0) {
    objClaim.ehts = strEhts;
  }

  if (curTime !== "") {
    objClaim.iat = rs.KJUR.jws.IntDate.get(curTime);
  }

  if (strUniq.length > 0) {
    objClaim.jti = strUniq;
  }

  var sClaim = JSON.stringify(objClaim);
  var alg = "RS256";
  var pHeader = { alg: alg, typ: "JWT" };
  var sHeader = JSON.stringify(pHeader);
  var privateKeyPemString = sessionStorage.getItem("pvtkey");

  strToken = rs.KJUR.jws.JWS.sign(null, sHeader, sClaim, privateKeyPemString);

  return strToken;
}

const createGUID = () => {
  function randomStr() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return (
    randomStr() +
    randomStr() +
    "-" +
    randomStr() +
    "-" +
    randomStr() +
    "-" +
    randomStr() +
    "-" +
    randomStr() +
    randomStr() +
    randomStr()
  );
}
